/**
 * EntitlementGate — RC 1.32 route protection.
 *
 * Sits between the authenticated shell (AppLayout) and the actual
 * workspace pages. Three outcomes:
 *
 *   1. Loading the plan state → show a short spinner inline.
 *   2. `has_entitlement === false` AND `unmigrated === false`
 *      → the user has not yet chosen a plan → redirect to
 *        `/onboarding/plan`.
 *   3. Otherwise → render children and, when status === 'expired',
 *      inject a top-of-page banner prompting renewal. Expired
 *      users still get access to the workspace so they can
 *      re-select; the RC 1.31 daily limit still governs them.
 *
 * Workspace scope (RC 1.33 fix + RC 1.35.E EMP-09):
 *   The gate only governs *candidate* workspace routes. Two
 *   exemption layers, in order:
 *     A. Pathname-prefix — `/employer/*`, `/admin/*`, `/post-login`,
 *        `/choose-workspace`, `/no-access`. Employer/admin shells
 *        never trigger the gate even on the first paint (avoids a
 *        spurious /api/account/plan call).
 *     B. Workspace membership — once `WorkspaceContext` resolves,
 *        if the user has no `candidate` access (i.e. a pure
 *        employer or admin), the gate becomes a no-op regardless
 *        of pathname. This is the EMP-09 fix: previously a pure
 *        employer hitting a candidate URL (e.g. `/jobs` via stale
 *        bookmark) was redirected to the candidate-flavoured
 *        `/onboarding/plan` BEFORE WorkspaceGuard got a chance to
 *        send them to `/employer`. Layer B defers to WorkspaceGuard
 *        so workspace routing wins.
 */
import { useEffect, useState } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { Loader2, TriangleAlert } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { track } from '@/lib/analytics';

// RC 1.45.PLAN-EVENTS — sessionStorage key used to ensure
// `plan_activated` fires exactly ONCE per (user, plan_code, session)
// instead of on every EntitlementGate refresh / page navigation.
const PLAN_ACTIVATED_SESSION_KEY = 'ndor_plan_activated_fired';

// Path prefixes that are OUT OF SCOPE for the candidate-plan gate.
// Employer routes live at /employer/*; admin routes at /admin/*;
// /post-login is the workspace-agnostic landing-resolver hub and
// MUST be free of the gate so employer/admin users can transit
// through it. /choose-workspace and /no-access are multi-workspace
// meta-screens with no candidate-plan surface. Onboarding routes
// are handled by ProtectedRoute upstream and don't reach this gate,
// but we keep them in the exemption list defensively.
const NON_CANDIDATE_PREFIXES = [
  '/employer',
  '/admin',
  '/post-login',
  '/choose-workspace',
  '/no-access',
];

export default function EntitlementGate({ children }) {
  const [plan, setPlan]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const { pathname }          = useLocation();
  const {
    allowedWorkspaces,
    loading: workspaceLoading,
    access,
  } = useWorkspace();

  const inCandidateScope      = !NON_CANDIDATE_PREFIXES.some((p) => pathname.startsWith(p));
  // EMP-09 — Layer B exemption. Once WorkspaceContext has resolved,
  // a user without `candidate` membership is a pure employer/admin.
  // We defer plan gating to WorkspaceGuard so they get sent to their
  // own workspace landing instead of /onboarding/plan.
  const hasCandidate = Array.isArray(allowedWorkspaces)
                       && allowedWorkspaces.includes('candidate');
  const workspaceResolved = !workspaceLoading && !!access;
  const skipGate = !inCandidateScope || (workspaceResolved && !hasCandidate);

  useEffect(() => {
    // On non-candidate routes — or for users with no candidate
    // workspace access — the gate is a no-op. Skip the plan fetch
    // entirely so the employer/admin shell renders without a
    // spinner or a stray API call.
    if (skipGate) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await apiFetch('/api/account/plan');
        if (!cancelled) {
          setPlan(p);
          // RC 1.45.PLAN-EVENTS — fire plan_activated exactly once
          // per (plan_code, session) when we observe an active paid
          // entitlement. Session-scoped flag ensures we don't spam the
          // event on every gate refresh / SPA route change.
          try {
            if (p?.status === 'active' && p?.plan_code && p.plan_code !== 'base') {
              const fired = sessionStorage.getItem(PLAN_ACTIVATED_SESSION_KEY);
              if (fired !== p.plan_code) {
                track('plan_activated', {
                  plan_code: p.plan_code,
                  source:    'entitlement_hydration',
                });
                sessionStorage.setItem(PLAN_ACTIVATED_SESSION_KEY, p.plan_code);
              }
            }
          } catch { /* sessionStorage unavailable or telemetry off — never throw */ }
        }
      } catch {
        // If the endpoint errors (network / auth flap), fail open so
        // we don't trap the user in a blank screen. They'll still be
        // bounced to /onboarding/plan on the next successful load.
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [skipGate]);

  // Skip-gate route → render children as-is, no gate, no banner.
  if (skipGate) return children;

  // Candidate-scope route, but workspace context still resolving →
  // brief spinner so we don't render redirect-prone state with stale
  // membership. Avoids a flash of /onboarding/plan for slow loaders.
  if (!workspaceResolved) {
    return (
      <div
        className="min-h-[240px] flex items-center justify-center text-ndor-ink-muted"
        data-testid="entitlement-gate-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="min-h-[240px] flex items-center justify-center text-ndor-ink-muted"
        data-testid="entitlement-gate-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Fail-open: if the account endpoint is down, let the user in.
  if (error) return children;

  // Fresh user, no plan selected yet.
  if (plan && plan.has_entitlement === false && plan.unmigrated === false) {
    return <Navigate to="/onboarding/plan" replace />;
  }

  const expired = plan?.status === 'expired';
  return (
    <>
      {expired && (
        <div
          className="mx-4 sm:mx-6 mt-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200 flex items-center justify-between gap-3"
          data-testid="entitlement-expired-banner"
        >
          <div className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4" />
            <span>Your plan has expired — renew to restore access.</span>
          </div>
          <Link
            to="/onboarding/plan"
            className="text-sm font-medium text-rose-200 hover:underline"
            data-testid="entitlement-expired-renew-link"
          >
            Renew
          </Link>
        </div>
      )}
      {children}
    </>
  );
}
